import React, { useState } from "react";
import Nav from "./Navigation.js";

const COLORS = [
  { bg: '#3b82f6', label: '1' }, // blue
  { bg: '#ef4444', label: '2' }, // red
  { bg: '#22c55e', label: '3' }, // green
  { bg: '#eab308', label: '4' }, // yellow
  { bg: '#a855f7', label: '5' }, // purple
  { bg: '#ec4899', label: '6' }, // pink
  { bg: '#6366f1', label: '7' }, // indigo
  { bg: '#f97316', label: '8' }, // orange
];

const Grid = ({ isModal = false, onClose }) => {
  const [cells, setCells] = useState(Array(16).fill(null)); // null | 0-7
  const [activeColor, setActiveColor] = useState(0);
  const [pendingCell, setPendingCell] = useState(null); // index of first cell of active pair

  const cellsOfColor = (ci) =>
    cells.reduce((acc, c, i) => { if (c === ci) acc.push(i); return acc; }, []);

  const isComplete = (ci) => cellsOfColor(ci).length >= 2;

  const nextAvailable = (from) => {
    for (let offset = 1; offset <= 8; offset++) {
      const c = (from + offset) % 8;
      if (!isComplete(c)) return c;
    }
    return null;
  };

  const allDone = COLORS.every((_, i) => isComplete(i));

  const handleLeftClick = (idx) => {
    if (allDone) return;
    if (cells[idx] !== null) return; // already marked — can't overwrite with left-click
    if (idx === pendingCell) return; // clicking the pending cell again does nothing

    if (pendingCell === null) {
      // First click of the pair
      setCells(prev => { const n = [...prev]; n[idx] = activeColor; return n; });
      setPendingCell(idx);
    } else {
      // Second click — completes the pair
      setCells(prev => { const n = [...prev]; n[idx] = activeColor; return n; });
      setPendingCell(null);
      const next = nextAvailable(activeColor);
      if (next !== null) setActiveColor(next);
    }
  };

  const handleRightClick = (idx, e) => {
    e.preventDefault();
    if (cells[idx] === null) return;

    const colorIdx = cells[idx];
    const others = cellsOfColor(colorIdx).filter(i => i !== idx);

    setCells(prev => { const n = [...prev]; n[idx] = null; return n; });
    setActiveColor(colorIdx);
    setPendingCell(others.length > 0 ? others[0] : null);
  };

  const handleSwatchClick = (ci) => {
    if (isComplete(ci)) return; // disabled
    const partial = cellsOfColor(ci);
    setActiveColor(ci);
    setPendingCell(partial.length === 1 ? partial[0] : null);
  };

  const handleReset = () => {
    setCells(Array(16).fill(null));
    setActiveColor(0);
    setPendingCell(null);
  };

  return (
    <div className="text-white">
      {!isModal && <Nav />}
      <div className="container mx-auto my-8 text-center px-4">

        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl lg:text-3xl font-bold flex-1">Matching Memory Helper</h1>
          {isModal && onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl ml-2" aria-label="Close">✕</button>
          )}
        </div>

        <p className="text-gray-400 text-sm mb-1">
          Left-click two cells to mark a pair. Once matched, automatically moves to the next pair.
        </p>
        <p className="text-gray-400 text-sm mb-5">
          Right-click a marked cell to remove it and resume that pair.
          Click a swatch to switch to a different pair.
        </p>

        {allDone && (
          <div className="mb-4 py-2 px-4 bg-green-900 bg-opacity-50 rounded-lg text-green-400 font-bold text-lg inline-block">
            All 8 pairs matched! 🎉
          </div>
        )}

        {/* 4×4 grid */}
        <div className="flex justify-center mb-6">
          <div className="inline-grid gap-1.5" style={{ gridTemplateColumns: 'repeat(4, 64px)' }}>
            {cells.map((colorIdx, i) => {
              const isEmpty = colorIdx === null;
              const isPending = i === pendingCell;
              const isActiveColorCell = !isEmpty && colorIdx === activeColor;

              return (
                <div
                  key={i}
                  onClick={() => handleLeftClick(i)}
                  onContextMenu={(e) => handleRightClick(i, e)}
                  style={{
                    width: 64, height: 64,
                    backgroundColor: isEmpty ? '#1f2937' : COLORS[colorIdx].bg,
                    border: isPending
                      ? '3px solid white'
                      : isEmpty
                      ? '2px dashed #4b5563'
                      : isActiveColorCell
                      ? '2px solid rgba(255,255,255,0.5)'
                      : '2px solid rgba(255,255,255,0.15)',
                    borderRadius: 6,
                    cursor: isEmpty ? 'pointer' : 'context-menu',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    userSelect: 'none',
                    boxShadow: isPending ? '0 0 8px rgba(255,255,255,0.4)' : 'none',
                    transition: 'background-color 0.15s',
                    position: 'relative',
                  }}
                >
                  {!isEmpty && (
                    <span style={{ color: 'white', fontWeight: 'bold', fontSize: 22, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
                      {COLORS[colorIdx].label}
                    </span>
                  )}
                  {/* White dot on pending cell */}
                  {isPending && (
                    <div style={{
                      position: 'absolute', top: 4, right: 4,
                      width: 7, height: 7,
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      boxShadow: '0 0 4px rgba(0,0,0,0.5)',
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Status line */}
        {!allDone && (
          <p className="text-sm mb-4" style={{ color: COLORS[activeColor].bg }}>
            <strong>Active: Pair {activeColor + 1}</strong>
            {pendingCell !== null
              ? ' — first cell placed, click a second cell'
              : ' — click any empty cell to start this pair'}
          </p>
        )}

        {/* Color swatches */}
        <div className="flex justify-center flex-wrap gap-2 mb-6">
          {COLORS.map((color, i) => {
            const count = cellsOfColor(i).length;
            const complete = isComplete(i);
            const isActive = i === activeColor && !complete;
            const isPartial = count === 1 && !complete;

            return (
              <div
                key={i}
                onClick={() => handleSwatchClick(i)}
                title={complete ? `Pair ${i+1}: complete` : isPartial ? `Pair ${i+1}: 1 of 2` : `Pair ${i+1}`}
                style={{
                  width: 54, height: 54,
                  backgroundColor: complete ? '#374151' : color.bg,
                  border: isActive
                    ? '3px solid white'
                    : isPartial
                    ? '3px solid #fbbf24'
                    : '2px solid transparent',
                  borderRadius: 8,
                  cursor: complete ? 'not-allowed' : 'pointer',
                  opacity: complete ? 0.35 : 1,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  userSelect: 'none',
                  transition: 'opacity 0.2s',
                  gap: 1,
                }}
              >
                <span style={{ color: 'white', fontWeight: 'bold', fontSize: 17, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                  {color.label}
                </span>
                {complete
                  ? <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>✓</span>
                  : isPartial
                  ? <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)' }}>½</span>
                  : null}
              </div>
            );
          })}
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-3">
          <button onClick={handleReset}
            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-5 rounded transition-colors">
            Reset
          </button>
          {isModal && onClose && (
            <button onClick={onClose}
              className="bg-gray-600 hover:bg-gray-500 text-white py-2 px-5 rounded transition-colors">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Grid;
